// unternehmensBegriff.ts — Benennung der Verbindungsliste nach der Rechtsform.
//
// Ein Verein führt keine „Verantwortung fürs Unternehmen", sondern eine
// „Verantwortung für den Verein" (PO-Punkt 1). Der Begriff ist ein STAMMDATUM:
// `ContactLegalForm.verbindungLabel` trägt „Unternehmen" (Default), „Verein",
// „Gemeinschaft", „Stiftung", „Genossenschaft" oder „Körperschaft" — betroffen
// sind 97 Firmen (41 Verein, 4 Stiftung, 52 Eigentümergemeinschaft, PROD).
//
// ⚠️ Hier entstehen ausschließlich ANZEIGETEXTE. Der Kontaktstatus „Mensch fürs
// Unternehmen" und der Beziehungstyp gleichen Namens sind GESPEICHERTE Werte und
// bleiben unverändert — sie werden an über 30 Stellen verglichen (z. B.
// ContactStatusPill, persistRelations, OrganigrammPopup) und würden brechen.

import * as React from "react";
import { fetchLegalFormOptions, type LegalFormOption } from "./legalFormOptions";

/** Fallback, wenn die Rechtsform fehlt oder kein `verbindungLabel` trägt. */
export const DEFAULT_BEGRIFF = "Unternehmen";

type Genus = "m" | "f" | "n";

/** Grammatisches Geschlecht der gesetzten `verbindungLabel`-Werte — es
 *  entscheidet über den Artikel („für den Verein" vs. „für die Stiftung"). */
const GENUS: Record<string, Genus> = {
  unternehmen: "n",
  verein: "m",
  gemeinschaft: "f",
  stiftung: "f",
  genossenschaft: "f",
  körperschaft: "f",
};

/**
 * `verbindungLabel` ist ein varchar(64) und über die Stammdatenpflege frei
 * belegbar — ein unbekannter Begriff darf deshalb keinen kaputten Satz ergeben.
 * Die deutschen Endungen -schaft/-ung/-heit/-keit/-ei/-ion/-tät sind ausnahmslos
 * feminin; alles übrige wird als Neutrum behandelt („fürs …"), weil das dem
 * Default „Unternehmen" entspricht.
 */
const genusOf = (begriff: string): Genus => {
  const key = begriff.trim().toLowerCase();
  return GENUS[key] ?? (/(schaft|ung|heit|keit|ei|ion|tät)$/.test(key) ? "f" : "n");
};

const normalize = (begriff?: string | null): string => (begriff ?? "").trim() || DEFAULT_BEGRIFF;

/**
 * Gehört der Gegenkontakt NICHT zu den Firmen im erwerbswirtschaftlichen Sinn?
 *
 * Genau dieselbe Unterscheidung, die `verbindungLabel` schon trifft: Der Default
 * „Unternehmen" steht an jeder erwerbswirtschaftlichen Rechtsform, jeder andere
 * gepflegte Wert an Verein, Stiftung, Genossenschaft, Gemeinschaft oder
 * Körperschaft. Es entsteht also KEIN zweites Merkmal — die Personenakte liest
 * nur das vorhandene Stammdatum.
 *
 * Bewusst als Negation benannt und nicht als „ist Körperschaft": Die ETG ist
 * eine Bruchteilsgemeinschaft (§§ 741 ff. BGB) und gerade KEINE Körperschaft —
 * und sie stellt mit 52 von 97 Sätzen die Mehrheit der betroffenen Firmen.
 */
export const istKeinUnternehmensBegriff = (begriff?: string | null): boolean =>
  normalize(begriff) !== DEFAULT_BEGRIFF;

/** „fürs Unternehmen" · „für den Verein" · „für die Stiftung". */
export const fuerDasUnternehmen = (begriff?: string | null): string => {
  const wort = normalize(begriff);
  const genus = genusOf(wort);
  if (genus === "n") return `fürs ${wort}`;
  return `für ${genus === "m" ? "den" : "die"} ${wort}`;
};

/** „im Unternehmen" · „im Verein" · „in der Stiftung". */
export const imDemUnternehmen = (begriff?: string | null): string => {
  const wort = normalize(begriff);
  return genusOf(wort) === "f" ? `in der ${wort}` : `im ${wort}`;
};

/** „Unternehmensleitung" · „Vereinsleitung" · „Stiftungsleitung". */
export const leitungTitel = (begriff?: string | null): string => {
  const wort = normalize(begriff);
  return `${wort}${/s$/i.test(wort) ? "" : "s"}leitung`;
};

/** Überschrift der Verbindungsliste: „Verantwortung fürs Unternehmen" bzw.
 *  „Verantwortung für den Verein". */
export const verantwortungTitel = (begriff?: string | null): string =>
  `Verantwortung ${fuerDasUnternehmen(begriff)}`;

/** Demonstrativ für die Beschreibungstexte: „dieses Unternehmen" (Akkusativ wie
 *  Nominativ), „diesen Verein" (Akk.) / „dieser Verein" (Nom.), „diese Stiftung". */
export const diesesUnternehmen = (
  begriff?: string | null,
  kasus: "nominativ" | "akkusativ" = "akkusativ",
): string => {
  const wort = normalize(begriff);
  const genus = genusOf(wort);
  if (genus === "n") return `dieses ${wort}`;
  if (genus === "f") return `diese ${wort}`;
  return `${kasus === "akkusativ" ? "diesen" : "dieser"} ${wort}`;
};

/** Der Katalogeintrag der Rechtsform — per Katalog-Id, ersatzweise über den
 *  Rechtsform-NAMEN (dort, wo nur das Label vorliegt, z. B. nach
 *  `mapContactRow`). Kein Treffer ⇒ `undefined`. */
export const rechtsformOptionAus = (
  options: LegalFormOption[],
  legalFormId?: number | null,
  legalFormName?: string | null,
): LegalFormOption | undefined => {
  const name = (legalFormName ?? "").trim().toLowerCase();
  return (
    (legalFormId != null ? options.find((o) => o.id === legalFormId) : undefined) ??
    (name ? options.find((o) => o.legalForm.trim().toLowerCase() === name) : undefined)
  );
};

/** Begriff aus einer bereits geladenen Optionsliste (siehe
 *  `rechtsformOptionAus`). Kein Treffer ⇒ Default. */
export const unternehmensBegriffAus = (
  options: LegalFormOption[],
  legalFormId?: number | null,
  legalFormName?: string | null,
): string => normalize(rechtsformOptionAus(options, legalFormId, legalFormName)?.verbindungLabel);

/**
 * Der Rechtsform-Katalog ändert sich innerhalb einer Sitzung nicht. Ohne diesen
 * Cache lädt ihn jede Liste und jeder Dialog erneut — die Überschrift hängt an
 * genau einem Stammdatensatz und ist keinen Request pro Zeile wert.
 * Ein Fehlschlag verwirft den Cache, damit der nächste Aufruf neu versucht.
 */
let katalogCache: Promise<LegalFormOption[]> | null = null;

export const loadLegalFormOptionsCached = (): Promise<LegalFormOption[]> => {
  if (!katalogCache) {
    katalogCache = fetchLegalFormOptions().catch((err) => {
      katalogCache = null;
      throw err;
    });
  }
  return katalogCache;
};

/** Nur für Tests: den Prozess-Cache zurücksetzen. */
export const resetLegalFormOptionsCache = (): void => {
  katalogCache = null;
};

/**
 * Begriff zur Rechtsform eines Kontakts. Ohne Rechtsform (Person, Firma ohne
 * Angabe) wird gar nicht geladen — die Antwort ist dann der Default.
 */
export const useUnternehmensBegriff = (
  legalFormId?: number | null,
  legalFormName?: string | null,
): string => {
  const [options, setOptions] = React.useState<LegalFormOption[]>([]);
  const name = (legalFormName ?? "").trim();

  React.useEffect(() => {
    if (legalFormId == null && !name) return;
    let aktiv = true;
    loadLegalFormOptionsCached()
      .then((geladen) => {
        if (aktiv) setOptions(geladen);
      })
      .catch(() => {
        /* Überschrift bleibt beim Default — kein Grund, die Ansicht zu stören. */
      });
    return () => {
      aktiv = false;
    };
  }, [legalFormId, name]);

  return React.useMemo(
    () => unternehmensBegriffAus(options, legalFormId, name),
    [options, legalFormId, name],
  );
};

/**
 * Begriff-Auflöser für LISTEN: eine Verbindungsliste enthält viele Gegenkontakte
 * mit je eigener Rechtsform. `useUnternehmensBegriff` je Zeile aufzurufen ginge
 * nicht (Hooks in Schleifen); der Katalog kommt deshalb einmal und wird hier zu
 * einer Funktion, die jede Zeile befragen kann.
 */
export const useUnternehmensBegriffe = (): ((
  legalFormId?: number | null,
  legalFormName?: string | null,
) => string) => {
  const [options, setOptions] = React.useState<LegalFormOption[]>([]);

  React.useEffect(() => {
    let aktiv = true;
    loadLegalFormOptionsCached()
      .then((geladen) => {
        if (aktiv) setOptions(geladen);
      })
      .catch(() => {
        /* ohne Katalog bleibt jede Zeile beim Default — kein Grund zu stören. */
      });
    return () => {
      aktiv = false;
    };
  }, []);

  return React.useCallback(
    (legalFormId?: number | null, legalFormName?: string | null) =>
      unternehmensBegriffAus(options, legalFormId, legalFormName),
    [options],
  );
};
